/**
 * Proxy test button component
 * Button to test proxy connectivity
 */

import { useState } from 'react';
import { Button, Tooltip } from 'antd';
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

interface ProxyTestButtonProps {
  onTest: () => Promise<boolean>;
  size?: 'small' | 'middle' | 'large';
  showLabel?: boolean;
}

type TestStatus = 'idle' | 'testing' | 'success' | 'failed';

export function ProxyTestButton({
  onTest,
  size = 'middle',
  showLabel = false,
}: ProxyTestButtonProps) {
  const [status, setStatus] = useState<TestStatus>('idle');

  const handleTest = async () => {
    setStatus('testing');
    try {
      const result = await onTest();
      // Just update visual status - hook handles notifications
      setStatus(result ? 'success' : 'failed');
    } catch (error) {
      setStatus('failed');
    }

    // Reset status after 3 seconds
    setTimeout(() => setStatus('idle'), 3000);
  };

  const getIcon = () => {
    switch (status) {
      case 'testing':
        return <LoadingOutlined />;
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <PlayCircleOutlined />;
    }
  };

  const getTooltip = () => {
    switch (status) {
      case 'testing':
        return 'Testing...';
      case 'success':
        return 'Test passed';
      case 'failed':
        return 'Test failed';
      default:
        return 'Test proxy';
    }
  };

  return (
    <Tooltip title={getTooltip()}>
      <Button
        type="text"
        size={size}
        icon={getIcon()}
        onClick={handleTest}
        disabled={status === 'testing'}
      >
        {showLabel && (
          status === 'testing' ? 'Testing...' : 'Test'
        )}
      </Button>
    </Tooltip>
  );
}

/**
 * Bulk proxy test button
 */
export function ProxyTestAllButton({
  onTestAll,
  proxyCount,
}: {
  onTestAll: () => Promise<{ passed: number; failed: number }>;
  proxyCount: number;
}) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ passed: number; failed: number } | null>(null);

  const handleTestAll = async () => {
    setTesting(true);
    setResult(null);
    try {
      const testResult = await onTestAll();
      // Just update visual status - hook handles notifications
      setResult(testResult);
    } catch (error) {
      // Hook handles error notification
    } finally {
      setTesting(false);
    }
  };

  return (
    <Button
      icon={testing ? <LoadingOutlined /> : <PlayCircleOutlined />}
      onClick={handleTestAll}
      disabled={testing || proxyCount === 0}
    >
      {testing
        ? 'Testing...'
        : result
        ? `${result.passed}/${proxyCount} OK`
        : `Test All (${proxyCount})`}
    </Button>
  );
}

export default ProxyTestButton;
